/**
 * EmailVerificationBanner.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { isEmailVerified } from '../utils/emailVerified';
import './EmailVerificationBanner.css';

const EmailVerificationBanner = () => {
  const { currentUser, updateCurrentUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const [isResending, setIsResending] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't show banner if:
  // 1. No user is logged in
  // 2. Email is already verified
  // 3. Banner was dismissed
  // 4. No token in localStorage (user is being logged out)
  const hasToken = localStorage.getItem('token') || localStorage.getItem('authToken');
  
  if (!currentUser || isEmailVerified(currentUser) || isDismissed || !hasToken) {
    return null;
  }

  const refreshCurrentUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const meRes = await fetch('/api/users/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (meRes.ok) {
      const userData = await meRes.json();
      updateCurrentUser({
        ...userData,
        email_verified: isEmailVerified(userData),
      });
    }
  };

  const isLocalDev = /localhost|127\.0\.0\.1|\[::1\]/i.test(window.location.hostname);

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email: currentUser.email })
      });

      const data = await response.json();

      if (response.ok && (data.success || data.alreadyVerified)) {
        if (data.alreadyVerified) {
          await refreshCurrentUser();
          showSuccess('Your email is already verified.');
        } else {
          if (data.devVerificationUrl && isLocalDev) {
            console.log('[EmailVerificationBanner] Dev verification link:', data.devVerificationUrl);
          }
          showSuccess(
            'Verification email sent! Open the most recent email — older links stop working after a resend. Check spam/junk too.'
          );
        }
      } else if (response.status === 429) {
        showError(data.error || 'Please wait a moment before requesting another email.');
      } else {
        showError(data.error || 'Failed to send verification email. Please try again.');
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      showError('Network error. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="email-verification-banner">
      <div className="verification-banner-content">
        <div className="verification-banner-icon">⚠️</div>
        <div className="verification-banner-message">
          <strong>Email Not Verified</strong>
          <span>Please check your email (including spam/junk) and click the verification link. If you resend, only the <strong>latest</strong> email will work.</span>
        </div>
        <div className="verification-banner-actions">
          <button 
            onClick={handleResendEmail} 
            disabled={isResending}
            className="resend-email-button"
          >
            {isResending ? 'Sending...' : 'Resend Email'}
          </button>
          <button 
            onClick={() => setIsDismissed(true)}
            className="dismiss-banner-button"
            aria-label="Dismiss banner"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;

